import { useState, useEffect, useRef } from "react";

const COURSES = ["DMLT", "BMLT", "Nursing", "BPharma"];

const SUBJECTS = {
  DMLT:    ["Haematology","Clinical Biochemistry","Microbiology","Histopathology","Blood Banking","Parasitology","Clinical Pathology","General"],
  BMLT:    ["Advanced Haematology","Immunology","Molecular Biology","Lab Management","Research Methodology","Quality Control","Biochemistry","General"],
  Nursing: ["Anatomy & Physiology","Nursing Foundations","Medical-Surgical Nursing","Obstetrics & Gynaecology","Pediatric Nursing","Community Health","Mental Health","Pharmacology","General"],
  BPharma: ["Pharmaceutics","Pharmacology","Pharmaceutical Chemistry","Pharmacognosy","Biochemistry","Drug Store Management","Human Anatomy & Physiology","General"],
};

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "saarthai@admin2024";

// ── Admin Login ────────────────────────────────────────────────────────────────
const AdminLogin = ({ onLogin }) => {
  const [pwd, setPwd]     = useState("");
  const [error, setError] = useState("");

  const handleLogin = () => {
    if (pwd === ADMIN_PASSWORD) { onLogin(); }
    else { setError("Incorrect admin password."); }
  };

  return (
    <div style={styles.center}>
      <div style={styles.loginCard}>
        <div style={styles.adminBadge}>🔐 Admin Portal</div>
        <h2 style={styles.loginTitle}>SaarthAI Admin</h2>
        <p style={styles.loginSub}>Course material management & RAG knowledge base</p>
        {error && <div style={styles.errorBox}>{error}</div>}
        <label style={styles.label}>Admin Password</label>
        <input
          type="password" placeholder="Enter admin password"
          value={pwd} onChange={e => { setPwd(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          style={styles.input} autoFocus
        />
        <button onClick={handleLogin} style={styles.primaryBtn}>Login →</button>
      </div>
    </div>
  );
};

// ── Main Admin Portal ──────────────────────────────────────────────────────────
export default function AdminPortal({ onBack, supabase }) {
  const [authed, setAuthed]           = useState(false);
  const [activeCourse, setActiveCourse] = useState("DMLT");
  const [materials, setMaterials]     = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [tab, setTab]                 = useState("upload"); // "upload" | "list"
  const [stats, setStats]             = useState({});
  const [msg, setMsg]                 = useState("");

  // Upload form state
  const [form, setForm] = useState({
    course: "DMLT", subject: "General",
    title: "", type: "notes", contentText: "", file: null,
  });
  const fileRef = useRef(null);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadMaterials = async (course) => {
    const { data } = await supabase
      .from("course_materials")
      .select("id,title,subject,type,status,created_at,storage_path")
      .eq("course", course)
      .order("created_at", { ascending: false });
    setMaterials(data || []);
  };

  const loadStats = async () => {
    const { data } = await supabase
      .from("course_materials")
      .select("course");
    const counts = {};
    (data || []).forEach(r => { counts[r.course] = (counts[r.course] || 0) + 1; });
    setStats(counts);
  };

  useEffect(() => {
    if (authed) { loadMaterials(activeCourse); loadStats(); }
  }, [authed, activeCourse]); // eslint-disable-line

  const handleUpload = async () => {
    if (!form.title.trim()) { setMsg("❌ Please enter a title."); return; }
    if (form.type !== "notes" && !form.file) { setMsg("❌ Please select a file."); return; }
    if (form.type === "notes" && !form.contentText.trim()) { setMsg("❌ Please enter the notes content."); return; }

    setUploading(true); setMsg("");

    let storagePath = null;

    // Upload file to Supabase Storage (videos/PDFs)
    if (form.file && form.type !== "notes") {
      const ext      = form.file.name.split(".").pop();
      const fileName = `${form.course}/${form.subject}/${Date.now()}_${form.title.replace(/\s+/g,"_")}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from("course-materials")
        .upload(fileName, form.file, { upsert: false });
      if (uploadErr) {
        setMsg(`❌ Upload failed: ${uploadErr.message}`); setUploading(false); return;
      }
      storagePath = fileName;
    }

    // Save metadata to DB
    const { error: dbErr } = await supabase.from("course_materials").insert({
      course:       form.course,
      subject:      form.subject,
      title:        form.title.trim(),
      type:         form.type,
      storage_path: storagePath,
      content_text: form.type === "notes" ? form.contentText.trim() : null,
      status:       form.type === "video" ? "pending_transcription" : "ready",
    });

    if (dbErr) {
      setMsg(`❌ Database error: ${dbErr.message}`); setUploading(false); return;
    }

    setMsg("✅ Material uploaded successfully!");
    setForm(f => ({ ...f, title:"", contentText:"", file:null }));
    if (fileRef.current) fileRef.current.value = "";
    loadMaterials(form.course);
    loadStats();
    setUploading(false);
  };

  const handleDelete = async (id, storagePath) => {
    if (!window.confirm("Delete this material?")) return;
    if (storagePath) {
      await supabase.storage.from("course-materials").remove([storagePath]);
    }
    await supabase.from("course_materials").delete().eq("id", id);
    loadMaterials(activeCourse);
    loadStats();
  };

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  return (
    <div style={styles.page}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarTop}>
          <div style={styles.sidebarBrand}>
            <div style={styles.adminIcon}>⚙️</div>
            <div>
              <div style={styles.sidebarTitle}>Admin Portal</div>
              <div style={styles.sidebarSub}>SaarthAI</div>
            </div>
          </div>
          <button onClick={onBack} style={styles.backBtn}>← Back to Site</button>
        </div>

        <div style={styles.courseList}>
          <div style={styles.sidebarLabel}>COURSES</div>
          {COURSES.map(c => (
            <button key={c} onClick={() => { setActiveCourse(c); setTab("list"); }}
              style={{ ...styles.courseBtn, ...(activeCourse===c && tab==="list" ? styles.courseBtnActive : {}) }}>
              <span>{c}</span>
              <span style={styles.statBadge}>{stats[c] || 0}</span>
            </button>
          ))}
        </div>

        <div style={styles.sidebarActions}>
          <div style={styles.sidebarLabel}>ACTIONS</div>
          <button onClick={() => setTab("upload")}
            style={{ ...styles.courseBtn, ...(tab==="upload" ? styles.courseBtnActive : {}) }}>
            📤 Upload Material
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={styles.main}>
        <div style={styles.topbar}>
          <h1 style={styles.pageTitle}>
            {tab === "upload" ? "Upload Course Material" : `${activeCourse} — Materials`}
          </h1>
          <div style={styles.topbarStats}>
            {COURSES.map(c => (
              <div key={c} style={styles.statCard}>
                <div style={styles.statNum}>{stats[c] || 0}</div>
                <div style={styles.statLabel}>{c}</div>
              </div>
            ))}
          </div>
        </div>

        {msg && (
          <div style={{ ...styles.msgBox, background: msg.startsWith("✅") ? "#EDFAF1" : "#FFF0F0",
            borderColor: msg.startsWith("✅") ? "#A8E6BC" : "#FFCDD2",
            color: msg.startsWith("✅") ? "#1A7340" : "#C0392B" }}>
            {msg}
          </div>
        )}

        {/* Upload Panel */}
        {tab === "upload" && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Add New Material to Knowledge Base</h2>
            <p style={styles.cardSub}>
              Text notes and PDFs are immediately available for Prakash Sir's RAG context.
              Videos are saved and marked for transcription.
            </p>

            <div style={styles.formGrid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Course</label>
                <select value={form.course}
                  onChange={e => setF("course", e.target.value)}
                  style={styles.input}>
                  {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Subject</label>
                <select value={form.subject}
                  onChange={e => setF("subject", e.target.value)}
                  style={styles.input}>
                  {(SUBJECTS[form.course] || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Title</label>
              <input type="text" placeholder="e.g. Introduction to Haematology"
                value={form.title} onChange={e => setF("title", e.target.value)}
                style={styles.input} />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Content Type</label>
              <div style={styles.typeGrid}>
                {[
                  { v:"notes",  icon:"📝", label:"Text Notes" },
                  { v:"pdf",    icon:"📄", label:"PDF / Doc" },
                  { v:"video",  icon:"🎥", label:"Video" },
                  { v:"slides", icon:"🖼️",  label:"Slides" },
                ].map(t => (
                  <button key={t.v} type="button" onClick={() => setF("type", t.v)}
                    style={{ ...styles.typeBtn, ...(form.type===t.v ? styles.typeBtnActive : {}) }}>
                    <span style={{ fontSize:20 }}>{t.icon}</span>
                    <span style={{ fontSize:12 }}>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {form.type === "notes" ? (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Notes Content (used directly for AI context)</label>
                <textarea
                  placeholder="Paste or type the study notes here. Prakash Sir will use this content when students ask related questions."
                  value={form.contentText}
                  onChange={e => setF("contentText", e.target.value)}
                  style={{ ...styles.input, minHeight:180, resize:"vertical", lineHeight:1.6 }}
                />
              </div>
            ) : (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Upload File</label>
                <input ref={fileRef} type="file"
                  accept={form.type==="video" ? "video/*" : form.type==="pdf" ? ".pdf,.doc,.docx" : ".ppt,.pptx,.pdf"}
                  onChange={e => setF("file", e.target.files[0])}
                  style={{ ...styles.input, padding:"10px 14px" }} />
                {form.type === "video" && (
                  <p style={{ fontSize:12, color:"#888", marginTop:6 }}>
                    ⚠️ Videos will be stored with status "Pending Transcription".
                    To enable AI context from videos, transcription must be set up separately.
                  </p>
                )}
              </div>
            )}

            <button onClick={handleUpload} disabled={uploading} style={styles.primaryBtn}>
              {uploading ? "⏳ Uploading..." : "📤 Upload to Knowledge Base"}
            </button>
          </div>
        )}

        {/* Materials List */}
        {tab === "list" && (
          <div style={styles.card}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h2 style={styles.cardTitle}>{activeCourse} Materials ({materials.length})</h2>
              <button onClick={() => setTab("upload")} style={styles.secondaryBtn}>+ Add Material</button>
            </div>

            {materials.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize:48, marginBottom:12 }}>📚</div>
                <p>No materials uploaded for {activeCourse} yet.</p>
                <button onClick={() => setTab("upload")} style={{ ...styles.primaryBtn, width:"auto", padding:"10px 24px" }}>
                  Upload First Material
                </button>
              </div>
            ) : (
              <div>
                {/* Group by subject */}
                {Object.entries(
                  materials.reduce((acc, m) => {
                    (acc[m.subject] = acc[m.subject] || []).push(m);
                    return acc;
                  }, {})
                ).map(([subject, items]) => (
                  <div key={subject} style={{ marginBottom:24 }}>
                    <div style={styles.subjectHeader}>{subject}</div>
                    {items.map(m => (
                      <div key={m.id} style={styles.materialRow}>
                        <div style={styles.materialIcon}>
                          {m.type==="video" ? "🎥" : m.type==="pdf" ? "📄" : m.type==="slides" ? "🖼️" : "📝"}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={styles.materialTitle}>{m.title}</div>
                          <div style={styles.materialMeta}>
                            {m.type} · {new Date(m.created_at).toLocaleDateString("en-IN")}
                          </div>
                        </div>
                        <div style={{
                          ...styles.statusBadge,
                          background: m.status==="ready" ? "#EDFAF1" : "#FFF8E7",
                          color:      m.status==="ready" ? "#1A7340" : "#8B6914",
                          borderColor:m.status==="ready" ? "#A8E6BC" : "#F0D080",
                        }}>
                          {m.status === "ready" ? "✅ Ready" : "⏳ Pending"}
                        </div>
                        <button onClick={() => handleDelete(m.id, m.storage_path)} style={styles.deleteBtn}>🗑</button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const styles = {
  page: { display:"flex", height:"100vh", fontFamily:"'Noto Sans',sans-serif", background:"#F0F4FF" },
  center: { display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#F0F4FF", padding:24 },
  loginCard: { background:"white", borderRadius:20, padding:"40px 36px", width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(27,58,107,0.15),0 0 0 1px #D4DCF0" },
  adminBadge: { display:"inline-block", background:"#EEF3FF", color:"#1B3A6B", fontSize:13, fontWeight:700, padding:"4px 12px", borderRadius:20, marginBottom:16 },
  loginTitle: { fontSize:24, fontWeight:800, color:"#0D1F3C", marginBottom:4 },
  loginSub:   { color:"#6B7FA3", fontSize:14, marginBottom:24 },
  errorBox:   { background:"#FFF0F0", border:"1px solid #FFCDD2", color:"#C0392B", borderRadius:10, padding:"10px 14px", fontSize:13, marginBottom:16 },
  label:      { display:"block", fontSize:12, fontWeight:600, color:"#6B7FA3", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" },
  input:      { width:"100%", padding:"12px 14px", border:"2px solid #D4DCF0", borderRadius:10, fontSize:14, fontFamily:"'Noto Sans',sans-serif", color:"#0D1F3C", background:"#F5F8FF", outline:"none", appearance:"none", transition:"border-color 0.2s" },
  primaryBtn: { width:"100%", padding:"13px", background:"linear-gradient(135deg,#1B3A6B,#2D5AA0)", color:"white", border:"none", borderRadius:12, fontSize:15, fontWeight:700, cursor:"pointer", marginTop:8, boxShadow:"0 4px 20px rgba(27,58,107,0.3)" },
  secondaryBtn:{ padding:"9px 18px", background:"#EEF3FF", color:"#1B3A6B", border:"1px solid #D4DCF0", borderRadius:10, fontSize:14, fontWeight:600, cursor:"pointer" },

  sidebar: { width:240, background:"#0D1F3C", display:"flex", flexDirection:"column", padding:0, flexShrink:0 },
  sidebarTop: { padding:"20px 16px", borderBottom:"1px solid rgba(255,255,255,0.08)" },
  sidebarBrand: { display:"flex", alignItems:"center", gap:12, marginBottom:16 },
  adminIcon: { fontSize:24 },
  sidebarTitle: { color:"white", fontWeight:700, fontSize:16 },
  sidebarSub: { color:"rgba(255,255,255,0.4)", fontSize:12 },
  backBtn: { background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", color:"rgba(255,255,255,0.6)", borderRadius:8, padding:"8px 12px", fontSize:13, cursor:"pointer", width:"100%" },
  courseList: { padding:"16px 12px", flex:1 },
  sidebarActions: { padding:"0 12px 16px" },
  sidebarLabel: { fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"1px", marginBottom:8, paddingLeft:8 },
  courseBtn: { display:"flex", justifyContent:"space-between", alignItems:"center", width:"100%", padding:"10px 12px", background:"none", border:"none", borderRadius:10, color:"rgba(255,255,255,0.65)", fontSize:14, fontWeight:500, cursor:"pointer", marginBottom:4, textAlign:"left" },
  courseBtnActive: { background:"rgba(45,90,160,0.4)", color:"white", fontWeight:700 },
  statBadge: { background:"rgba(255,255,255,0.12)", color:"rgba(255,255,255,0.6)", fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:10 },

  main: { flex:1, overflow:"auto", padding:28 },
  topbar: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24, flexWrap:"wrap", gap:16 },
  pageTitle: { fontSize:22, fontWeight:800, color:"#0D1F3C" },
  topbarStats: { display:"flex", gap:12 },
  statCard: { background:"white", borderRadius:12, padding:"10px 16px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", border:"1px solid #E8EDF8", minWidth:60 },
  statNum: { fontSize:22, fontWeight:800, color:"#1B3A6B" },
  statLabel: { fontSize:11, color:"#6B7FA3", fontWeight:600 },
  msgBox: { borderRadius:10, border:"1px solid", padding:"12px 16px", fontSize:14, marginBottom:20 },

  card: { background:"white", borderRadius:16, padding:"28px 28px", boxShadow:"0 2px 16px rgba(0,0,0,0.06)", border:"1px solid #E8EDF8" },
  cardTitle: { fontSize:18, fontWeight:700, color:"#0D1F3C", marginBottom:6 },
  cardSub: { fontSize:14, color:"#6B7FA3", marginBottom:24, lineHeight:1.6 },
  formGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 },
  fieldGroup: { marginBottom:16 },
  typeGrid: { display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginTop:6 },
  typeBtn: { display:"flex", flexDirection:"column", alignItems:"center", gap:4, padding:"12px 8px", border:"2px solid #D4DCF0", borderRadius:10, background:"#F5F8FF", cursor:"pointer", transition:"all 0.15s" },
  typeBtnActive: { border:"2px solid #1B3A6B", background:"#EEF3FF" },

  subjectHeader: { fontSize:12, fontWeight:700, color:"#6B7FA3", textTransform:"uppercase", letterSpacing:"0.8px", padding:"8px 0 6px", borderBottom:"1px solid #E8EDF8", marginBottom:8 },
  materialRow: { display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:"1px solid #F0F4FF" },
  materialIcon: { fontSize:20, width:32, textAlign:"center" },
  materialTitle: { fontSize:14, fontWeight:600, color:"#0D1F3C" },
  materialMeta: { fontSize:12, color:"#9BACC8", marginTop:2 },
  statusBadge: { fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, border:"1px solid", flexShrink:0 },
  deleteBtn: { background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#C0392B", opacity:0.6, padding:"4px 6px", borderRadius:6 },
  emptyState: { textAlign:"center", padding:"48px 20px", color:"#6B7FA3" },
};
